import { getRegionSettings } from './getRegionSettings.ts'
export const LAND_SUPPORTED_LANGUAGES = ['az', 'en', 'ky', 'kz', 'ru', 'tr', 'uz'] as const
export const SUPPORTED_GEO = ['KZ', 'AZ', 'UZ', 'KG'] as const
export type SupportedLanguage = (typeof LAND_SUPPORTED_LANGUAGES)[number]
export type SupportedGeo = (typeof SUPPORTED_GEO)[number]

export type ProjectInfoEntry = {
  project_id: number
  project_uuid: string
  currencies: string[]
  geo_main: string[]
  geo_main_alpha_3: string[]
  locales: string[]
  cda: string
}

export type IpInfoResponse = {
  alpha_3?: string
  alpha_2?: string
}

export type ProjectGeoConfig = {
  languages: string[]
  currency: string
} & Pick<ProjectInfoEntry, 'cda' | 'project_id' | 'project_uuid'>

export type DecorativeAsset = {
  file: string
  className: string
  alt?: string
  fetchPriority?: 'high' | 'low' | 'auto'
}
export type MainElementConfig = {
  element: {
    desktop: string
    tablet?: string
    mobile?: string
    className: string
  }
  fetchPriority?: 'high' | 'low' | 'auto'
}

type LayoutConfig = {
  background: {
    mobileFile: string
    desktopFile: string
    className: string
    alt?: string
    fetchPriority?: 'high' | 'low' | 'auto'
  }
  payment: MainElementConfig
  girl: MainElementConfig
  prize: MainElementConfig
  wheel: MainElementConfig
  decorElements: {
    desktop?: DecorativeAsset[]
    tablet?: DecorativeAsset[]
    mobile?: DecorativeAsset[]
  }
  currency: string
}
type PhoneConfig = {
  code: SupportedGeo
  dial: string
  maxLength: number
  placeholder: string
  flagSrc: string
  flagAlt: string
}
export type GeoConfig = {
  id: SupportedGeo
  phone: PhoneConfig
  layout: LayoutConfig
}

export type GeoRuntimeSettings = {
  defaultLanguage: SupportedLanguage
  supportedLanguages: SupportedLanguage[]
  countryOfResidence: string
  currency: string
}

export type GeoRuntimeConfig = GeoConfig & GeoRuntimeSettings

export const DEFAULT_GEO: SupportedGeo = SUPPORTED_GEO[0]
export const GEO_STORAGE_KEY = 'preferredGeo'
export const LANGUAGE_STORAGE_KEY = 'preferredLanguage' // already used in index.js
export const GEO_QUERY_KEY = 'geo'

export const LANGUAGE_TO_GEO: Partial<Record<SupportedLanguage, SupportedGeo>> = {
  az: 'AZ',
  tr: 'AZ',
  ky: 'KG',
  kz: 'KZ',
  uz: 'UZ',
}

export const LANGUAGE_ALIASES: Record<string, SupportedLanguage> = {
  kk: 'kz',
}

export const LANGUAGE_META: Record<SupportedLanguage, { flag: string; label: string }> = {
  az: { flag: '/flags/az.svg', label: 'AZ' },
  en: { flag: '/flags/en.svg', label: 'EN' },
  ky: { flag: '/flags/kg.svg', label: 'KG' },
  kz: { flag: '/flags/kz.svg', label: 'KZ' },
  ru: { flag: '/flags/ru.svg', label: 'RU' },
  tr: { flag: '/flags/tr.svg', label: 'TR' },
  uz: { flag: '/flags/uz.svg', label: 'UZ' },
}

export const GEO_META: Record<SupportedGeo, { flag: string; label: string }> = {
  AZ: { flag: '/flags/az.svg', label: 'AZ' },
  KG: { flag: '/flags/kg.svg', label: 'KG' },
  KZ: { flag: '/flags/kz.svg', label: 'KZ' },
  UZ: { flag: '/flags/uz.svg', label: 'UZ' },
}
export const getCommonAssetPath = (file: string) => `/assets/img/${file}`

export const getGeoAssetPath = (geo: SupportedGeo, file: string) =>
  `/assets/img/${geo.toLowerCase()}/${file}`

export const GEO_RUNTIME_FALLBACKS: Record<SupportedGeo, GeoRuntimeSettings> = {
  AZ: {
    defaultLanguage: 'az',
    supportedLanguages: ['ru', 'az', 'tr', 'en'],
    countryOfResidence: 'AZE',
    currency: 'AZN',
  },
  KG: {
    defaultLanguage: 'ru',
    supportedLanguages: ['ru', 'ky', 'en'],
    countryOfResidence: 'KGZ',
    currency: 'KGS',
  },
  KZ: {
    defaultLanguage: 'kz',
    supportedLanguages: ['ru', 'kz', 'en'],
    countryOfResidence: 'KAZ',
    currency: 'KZT',
  },
  UZ: {
    defaultLanguage: 'uz',
    supportedLanguages: ['ru', 'uz', 'en'],
    countryOfResidence: 'UZB',
    currency: 'UZS',
  },
}

export const normalizeGeo = (value: string | null | undefined): SupportedGeo | null => {
  if (!value) return null

  const normalized = value.trim().toUpperCase()

  return SUPPORTED_GEO.some((geo) => geo === normalized) ? (normalized as SupportedGeo) : null
}

export const normalizeLanguage = (value: string | null | undefined): SupportedLanguage | null => {
  if (!value) return null

  const normalized = value.trim().toLowerCase()
  const shortCode = normalized.split('-')[0]
  const aliased = LANGUAGE_ALIASES[shortCode] ?? shortCode

  return LAND_SUPPORTED_LANGUAGES.includes(aliased) ? (aliased as SupportedLanguage) : null
}

export const GEO_CONFIG: Record<SupportedGeo, GeoConfig> = {
  AZ: {
    id: 'AZ',
    phone: {
      code: 'AZ',
      dial: '994',
      maxLength: 13,
      placeholder: '+994 XX XXX XX XX',
      flagSrc: getGeoAssetPath('AZ', 'phone.svg'),
      flagAlt: 'AZ flag',
    },

    layout: {
      background: {
        mobileFile: 'mobile-bg.webp',
        desktopFile: 'desktop-bg.webp',
        className: 'backgroundImage',
        alt: 'background image',
        fetchPriority: 'high',
      },

      payment: {
        element: {
          desktop: 'paymentDesktop.webp',
          tablet: 'paymentDesktop.webp',
          mobile: 'paymentMobile.webp',
          className: 'payment',
        },
        fetchPriority: 'high',
      },
      girl: {
        element: {
          desktop: 'girlDesktop.webp',
          tablet: 'girlDesktop.webp',
          className: 'girl',
        },
        fetchPriority: 'high',
      },
      prize: {
        element: {
          desktop: 'prize.webp',
          className: 'prize',
        },
        fetchPriority: 'high',
      },
      wheel: {
        element: {
          desktop: 'wheel.webp',
          className: 'wheel',
        },
        fetchPriority: 'high',
      },
      decorElements: {
        mobile: [
          {
            file: 'highlightLeftMobile.webp',
            className: 'highlightLeftMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightMobile.webp',
            className: 'highlightRightMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorMobile',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        tablet: [
          {
            file: 'highlightLeftDesktop.webp',
            className: 'highlightLeft',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightDesktop.webp',
            className: 'highlightRight',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'decorDesktop.webp',
            className: 'decorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        desktop: [
          {
            file: 'decorDesktop.webp',
            className: 'decorDesktop',
            alt: 'decor',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft.webp',
            className: 'bgLeft',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft2.webp',
            className: 'bgLeft2',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgRight.webp',
            className: 'bgRight',
            alt: 'bgRight',
            fetchPriority: 'high',
          },
          { file: 'chips1.webp', className: 'chips1', alt: 'Chips' },
          { file: 'chips2.webp', className: 'chips2', alt: 'Chips 2' },
          { file: 'chips2.webp', className: 'chips3', alt: 'Chips 2' },
          { file: 'highlightLeftDesktop.webp', className: 'highlightLeft', alt: 'highlight' },
          { file: 'highlightRightDesktop.webp', className: 'highlightRight', alt: 'highlight' },
        ],
      },
      currency: 'currencyAz.svg',
    },
  },
  KG: {
    id: 'KG',
    phone: {
      code: 'KG',
      dial: '996',
      maxLength: 13,
      placeholder: '+996 XXX XXX XXX',
      flagSrc: getGeoAssetPath('KG', 'phone.svg'),
      flagAlt: 'KG flag',
    },

    layout: {
      background: {
        mobileFile: 'mobile-bg.webp',
        desktopFile: 'desktop-bg.webp',
        className: 'backgroundImage',
        alt: 'background image',
        fetchPriority: 'high',
      },

      payment: {
        element: {
          desktop: 'paymentDesktop.webp',
          tablet: 'paymentDesktop.webp',
          mobile: 'paymentMobile.webp',
          className: 'payment',
        },
        fetchPriority: 'high',
      },
      girl: {
        element: {
          desktop: 'girlDesktop.webp',
          tablet: 'girlDesktop.webp',
          className: 'girl',
        },
        fetchPriority: 'high',
      },
      prize: {
        element: {
          desktop: 'prize.webp',
          className: 'prize',
        },
        fetchPriority: 'high',
      },
      wheel: {
        element: {
          desktop: 'wheel.webp',
          className: 'wheel',
        },
        fetchPriority: 'high',
      },
      decorElements: {
        mobile: [
          {
            file: 'highlightLeftMobile.webp',
            className: 'highlightLeftMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightMobile.webp',
            className: 'highlightRightMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorMobile',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        tablet: [
          {
            file: 'highlightLeftDesktop.webp',
            className: 'highlightLeft',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightDesktop.webp',
            className: 'highlightRight',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'decorDesktop.webp',
            className: 'decorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        desktop: [
          {
            file: 'decorDesktop.webp',
            className: 'decorDesktop',
            alt: 'decor',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft.webp',
            className: 'bgLeft',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft2.webp',
            className: 'bgLeft2',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgRight.webp',
            className: 'bgRight',
            alt: 'bgRight',
            fetchPriority: 'high',
          },
          { file: 'chips1.webp', className: 'chips1', alt: 'Chips' },
          { file: 'chips2.webp', className: 'chips2', alt: 'Chips 2' },
          { file: 'chips2.webp', className: 'chips3', alt: 'Chips 2' },
          { file: 'highlightLeftDesktop.webp', className: 'highlightLeft', alt: 'highlight' },
          { file: 'highlightRightDesktop.webp', className: 'highlightRight', alt: 'highlight' },
        ],
      },
      currency: 'currencyKg.svg',
    },
  },
  UZ: {
    id: 'UZ',
    phone: {
      code: 'UZ',
      dial: '998',
      maxLength: 13,
      placeholder: '+998 XX XXX XX XX',
      flagSrc: getGeoAssetPath('UZ', 'phone.svg'),
      flagAlt: 'UZ flag',
    },

    layout: {
      background: {
        mobileFile: 'mobile-bg.webp',
        desktopFile: 'desktop-bg.webp',
        className: 'backgroundImage',
        alt: 'background image',
        fetchPriority: 'high',
      },

      payment: {
        element: {
          desktop: 'paymentDesktop.webp',
          tablet: 'paymentDesktop.webp',
          mobile: 'paymentMobile.webp',
          className: 'payment',
        },
        fetchPriority: 'high',
      },
      girl: {
        element: {
          desktop: 'girlDesktop.webp',
          tablet: 'girlDesktop.webp',
          className: 'girl',
        },
        fetchPriority: 'high',
      },
      prize: {
        element: {
          desktop: 'prize.webp',
          className: 'prize',
        },
        fetchPriority: 'high',
      },
      wheel: {
        element: {
          desktop: 'wheel.webp',
          className: 'wheel',
        },
        fetchPriority: 'high',
      },
      decorElements: {
        mobile: [
          {
            file: 'highlightLeftMobile.webp',
            className: 'highlightLeftMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightMobile.webp',
            className: 'highlightRightMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorMobile',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        tablet: [
          {
            file: 'highlightLeftDesktop.webp',
            className: 'highlightLeft',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightDesktop.webp',
            className: 'highlightRight',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'decorDesktop.webp',
            className: 'decorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        desktop: [
          {
            file: 'decorDesktop.webp',
            className: 'decorDesktop',
            alt: 'decor',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft.webp',
            className: 'bgLeft',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft2.webp',
            className: 'bgLeft2',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgRight.webp',
            className: 'bgRight',
            alt: 'bgRight',
            fetchPriority: 'high',
          },
          { file: 'chips1.webp', className: 'chips1', alt: 'Chips' },
          { file: 'chips2.webp', className: 'chips2', alt: 'Chips 2' },
          { file: 'chips2.webp', className: 'chips3', alt: 'Chips 2' },
          { file: 'highlightLeftDesktop.webp', className: 'highlightLeft', alt: 'highlight' },
          { file: 'highlightRightDesktop.webp', className: 'highlightRight', alt: 'highlight' },
        ],
      },
      currency: 'currencyUz.svg',
    },
  },
  KZ: {
    id: 'KZ',
    phone: {
      code: 'KZ',
      dial: '7',
      maxLength: 12,
      placeholder: '+7 XXX XXX XXXX',
      flagSrc: getGeoAssetPath('KZ', 'phone.svg'),
      flagAlt: 'KZ flag',
    },

    layout: {
      background: {
        mobileFile: 'mobile-bg.webp',
        desktopFile: 'desktop-bg.webp',
        className: 'backgroundImage',
        alt: 'background image',
        fetchPriority: 'high',
      },

      payment: {
        element: {
          desktop: 'paymentDesktop.webp',
          tablet: 'paymentDesktop.webp',
          mobile: 'paymentMobile.webp',
          className: 'payment',
        },
        fetchPriority: 'high',
      },
      girl: {
        element: {
          desktop: 'girlDesktop.webp',
          tablet: 'girlDesktop.webp',
          className: 'girl',
        },
        fetchPriority: 'high',
      },
      prize: {
        element: {
          desktop: 'prize.webp',
          className: 'prize',
        },
        fetchPriority: 'high',
      },
      wheel: {
        element: {
          desktop: 'wheel.webp',
          className: 'wheel',
        },
        fetchPriority: 'high',
      },
      decorElements: {
        mobile: [
          {
            file: 'highlightLeftMobile.webp',
            className: 'highlightLeftMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightMobile.webp',
            className: 'highlightRightMobile',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorMobile',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        tablet: [
          {
            file: 'highlightLeftDesktop.webp',
            className: 'highlightLeft',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'highlightRightDesktop.webp',
            className: 'highlightRight',
            alt: 'highlight',
            fetchPriority: 'auto',
          },
          {
            file: 'decorDesktop.webp',
            className: 'decorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
          {
            file: 'bgDecorTablet.webp',
            className: 'bgDecorTablet',
            alt: 'decor',
            fetchPriority: 'auto',
          },
        ],
        desktop: [
          {
            file: 'decorDesktop.webp',
            className: 'decorDesktop',
            alt: 'decor',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft.webp',
            className: 'bgLeft',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgLeft2.webp',
            className: 'bgLeft2',
            alt: 'bgLeft',
            fetchPriority: 'high',
          },
          {
            file: 'bgRight.webp',
            className: 'bgRight',
            alt: 'bgRight',
            fetchPriority: 'high',
          },
          { file: 'chips1.webp', className: 'chips1', alt: 'Chips' },
          { file: 'chips2.webp', className: 'chips2', alt: 'Chips 2' },
          { file: 'chips2.webp', className: 'chips3', alt: 'Chips 2' },
          { file: 'highlightLeftDesktop.webp', className: 'highlightLeft', alt: 'highlight' },
          { file: 'highlightRightDesktop.webp', className: 'highlightRight', alt: 'highlight' },
        ],
      },
      currency: 'currencyKz.svg',
    },
  },
}

export type ResolvedGeoState = {
  geo: SupportedGeo
  language: SupportedLanguage
  projectId: number | string | undefined
  baseCdaUrl: string | undefined
  allowedRegions: SupportedGeo[]
  allowedLanguages: SupportedLanguage[]
}

const getProjectInfoLanguages = (item: ProjectInfoEntry | undefined): SupportedLanguage[] => {
  if (!item) return []

  return Array.from(
    new Set(
      item.locales
        .map((locale) => normalizeLanguage(locale))
        .filter(Boolean) as SupportedLanguage[],
    ),
  )
}

export const getGeoRuntimeConfig = (
  projectInfo: ProjectInfoEntry[],
  geo: SupportedGeo,
): GeoRuntimeConfig => {
  const config = GEO_CONFIG[geo]
  const fallback = GEO_RUNTIME_FALLBACKS[geo]
  const infoByGeo = getProjectInfoByGeo(projectInfo, geo)
  const supportedLanguages = getProjectInfoLanguages(infoByGeo)

  return {
    ...config,
    supportedLanguages: supportedLanguages.length
      ? supportedLanguages
      : fallback.supportedLanguages,
    defaultLanguage: supportedLanguages[0] ?? fallback.defaultLanguage,
    countryOfResidence: infoByGeo?.geo_main_alpha_3[0] ?? fallback.countryOfResidence,
    currency: infoByGeo?.currencies[0] ?? fallback.currency,
  }
}

export const getResolvedLanguageForGeo = (
  geo: SupportedGeo,
  language: SupportedLanguage,
  projectInfo: ProjectInfoEntry[] = [],
) => {
  const runtimeConfig = getGeoRuntimeConfig(projectInfo, geo)

  return runtimeConfig.supportedLanguages.includes(language)
    ? language
    : runtimeConfig.defaultLanguage
}

export const getResolvedGeoStateForGeo = ({
  geo,
  language,
  projectInfo = [],
  allowedRegions,
  allowedLanguages,
}: {
  geo: SupportedGeo
  language: SupportedLanguage
  projectInfo?: ProjectInfoEntry[]
  allowedRegions: SupportedGeo[]
  allowedLanguages: SupportedLanguage[]
}): ResolvedGeoState => {
  const infoByGeo = getProjectInfoByGeo(projectInfo, geo)

  return {
    geo,
    language: getResolvedLanguageForGeo(geo, language, projectInfo),
    projectId: infoByGeo?.project_id,
    baseCdaUrl: infoByGeo?.cda,
    allowedRegions,
    allowedLanguages,
  }
}

const getAllowedRegions = (allowedRegions: Set<string>): SupportedGeo[] =>
  Array.from(allowedRegions)
    .map((region) => normalizeGeo(region))
    .filter((region): region is SupportedGeo => Boolean(region))

const getAllowedLanguages = (allowedLanguages: Set<string>): SupportedLanguage[] =>
  Array.from(allowedLanguages)
    .map((language) => normalizeLanguage(language))
    .filter((language): language is SupportedLanguage => Boolean(language))

const REGION_SETTINGS = {
  languageToRegion: {
    kz: 'KZ',
    az: 'AZ',
    tr: 'AZ',
    uz: 'UZ',
    kg: 'KG',
  },
  ianaToRegion: {
    'Asia/Almaty': { region: 'KZ', offsetHours: 5 },
    'Asia/Qyzylorda': { region: 'KZ', offsetHours: 5 },
    'Asia/Aqtau': { region: 'KZ', offsetHours: 5 },
    'Asia/Aqtobe': { region: 'KZ', offsetHours: 5 },
    'Asia/Atyrau': { region: 'KZ', offsetHours: 5 },
    'Asia/Oral': { region: 'KZ', offsetHours: 5 },
    'Asia/Baku': { region: 'AZ', offsetHours: 4 },
    'Asia/Tashkent': { region: 'UZ', offsetHours: 5 },
    'Asia/Samarkand': { region: 'UZ', offsetHours: 5 },
    'Asia/Bishkek': { region: 'KG', offsetHours: 6 },
  },
  utcOffsetTable: {
    4: 'AZ',
    6: 'KG',
  },
}

const filterRegionConfig = (
  regionConfig: Partial<Record<SupportedGeo, ProjectGeoConfig>>,
  allowedGeo: SupportedGeo[],
) => {
  if (!allowedGeo?.length) return regionConfig
  const filteredRegionConfig = Object.keys(regionConfig).reduce<
    Partial<Record<SupportedGeo, ProjectGeoConfig>>
  >((acc, region) => {
    if (allowedGeo.includes(region as SupportedGeo))
      acc[region as SupportedGeo] = regionConfig[region as SupportedGeo]
    return acc
  }, {})
  if (Object.keys(filteredRegionConfig).length) return filteredRegionConfig
  if (regionConfig[DEFAULT_GEO]) return { [DEFAULT_GEO]: regionConfig[DEFAULT_GEO] }

  return regionConfig
}

const buildRegionConfigFromProjectInfo = (projectInfo: ProjectInfoEntry[]) =>
  projectInfo.reduce<Partial<Record<SupportedGeo, ProjectGeoConfig>>>((acc, item) => {
    const region = item.geo_main[0]
    const currency = item.currencies[0]
    const languages = item.locales

    if (region && currency && languages.length) {
      acc[region as SupportedGeo] = {
        cda: item.cda,
        project_id: item.project_id,
        project_uuid: item.project_uuid,
        languages,
        currency,
      }
    }

    return acc
  }, {})

export const getProjectInfoByGeo = (projectInfo: ProjectInfoEntry[], geo: SupportedGeo) =>
  projectInfo.find((item) => normalizeGeo(item.geo_main[0]) === geo)

const getAllowedGeoQuery = (searchParams: URLSearchParams) => {
  const geoValues = searchParams.getAll(GEO_QUERY_KEY)

  return [
    ...new Set(
      geoValues
        .flatMap((value) => value.split(','))
        .map((value) => value.trim().toUpperCase())
        .filter((value): value is SupportedGeo => SUPPORTED_GEO.some((geo) => geo === value)),
    ),
  ]
}

export const resolveGeoState = ({
  search,
  storedGeo,
  storedLanguage,
  projectInfo,
  ipAlpha2,
}: {
  search: string
  storedGeo?: string | null
  storedLanguage?: string | null
  projectInfo: ProjectInfoEntry[]
  ipAlpha2?: string
}): ResolvedGeoState => {
  const allowedGeo = getAllowedGeoQuery(new URLSearchParams(search))
  const regionConfig = filterRegionConfig(buildRegionConfigFromProjectInfo(projectInfo), allowedGeo)

  // todo remove?
  if (!Object.keys(regionConfig).length) {
    // todo
  }

  const data = getRegionSettings(
    {
      ...REGION_SETTINGS,
      regionConfig,
    },
    {
      langSaved: normalizeLanguage(storedLanguage),
      regionSaved: normalizeGeo(storedGeo),
      navigatorLanguages:
        typeof navigator !== 'undefined'
          ? ([navigator.language, ...(navigator.languages ?? [])]
              .map((language) => normalizeLanguage(language))
              .filter(Boolean) as SupportedLanguage[])
          : [],
      utcOffsetHours:
        typeof window !== 'undefined' ? -new Date().getTimezoneOffset() / 60 : undefined,
      ianaTimeZone:
        typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
      ipAlpha2,
    },
  )

  const geo = normalizeGeo(data.region) ?? DEFAULT_GEO
  const geoRuntimeConfig = getGeoRuntimeConfig(projectInfo, geo)
  const language = getResolvedLanguageForGeo(
    geo,
    normalizeLanguage(data.language) ?? geoRuntimeConfig.defaultLanguage,
    projectInfo,
  )
  const allowedRegions = getAllowedRegions(data.allowedRegions)
  const allowedLanguages = getAllowedLanguages(data.allowedLang)

  return {
    geo,
    language,
    projectId: data.config?.project_id,
    baseCdaUrl: data.config?.cda,
    allowedRegions,
    allowedLanguages,
  }
}
